import React, { useState } from 'react';
import { Download, Loader2, FileText, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import * as apiClient from '@/services/apiClient';

/**
 * Downloading a resume as a typeset PDF — "Resume only", or "Resume +
 * evaluation" with EchoHire's assessment on its own internal page.
 *
 * One component for every place a resume appears — the report's Resume tab,
 * the Resumes page cards, its list rows and its detail view — so the options,
 * wording and error handling cannot drift apart between them.
 *
 * Only offered once evaluation has completed: before that the parsed data is
 * partial and the document would be a misleading half-resume. The server
 * refuses too. It is also, for most resumes, the only copy there is — the
 * uploaded file is deleted when evaluation finishes.
 */

export const canDownloadResume = (resume) => resume?.status === 'completed';

function useResumeDownload(resumeId) {
  // 'resume' | 'eval' while in flight, so the right control shows a spinner
  const [downloading, setDownloading] = useState(null);

  const download = async (evaluation) => {
    if (!resumeId || downloading) return;
    setDownloading(evaluation ? 'eval' : 'resume');
    try {
      await apiClient.downloadResumePdf(resumeId, { evaluation });
    } catch (err) {
      toast.error(err.message || 'Could not download the resume.');
    } finally {
      setDownloading(null);
    }
  };

  return { downloading, download };
}

/** Two labelled buttons — for places with room: detail views, the report tab. */
export function ResumeDownloadButtons({ resume, className }) {
  const { downloading, download } = useResumeDownload(resume?._id);
  if (!canDownloadResume(resume)) return null;

  const icon = (kind) => (downloading === kind
    ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
    : <Download className="h-3.5 w-3.5" aria-hidden />);

  return (
    <div className={className ?? 'flex flex-wrap gap-2'}>
      <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs"
        disabled={downloading !== null} onClick={() => download(false)}>
        {icon('resume')}Resume only
      </Button>
      <Button size="sm" className="h-8 gap-1.5 text-xs"
        disabled={downloading !== null} onClick={() => download(true)}
        title="Adds EchoHire's evaluation as a separate, internal page">
        {icon('eval')}Resume + evaluation
      </Button>
    </div>
  );
}

/** One icon that opens both options — for dense places: cards and list rows. */
export function ResumeDownloadMenu({ resume }) {
  const { downloading, download } = useResumeDownload(resume?._id);
  if (!canDownloadResume(resume)) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-primary"
          aria-label="Download resume" title="Download resume" disabled={downloading !== null}>
          {downloading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
            : <Download className="h-3.5 w-3.5" aria-hidden />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-[11px] font-medium text-muted-foreground">
          Download as PDF
        </DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => download(false)} className="gap-2 text-xs">
          <FileText className="h-3.5 w-3.5" aria-hidden />
          <span>Resume only</span>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => download(true)} className="gap-2 text-xs">
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          <span className="flex flex-col">
            <span>Resume + evaluation</span>
            <span className="text-[10px] text-muted-foreground">Adds an internal assessment page</span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
